<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import {
  resolveThreadListRow,
  resolveThreadListRowFromFetched,
  hasThreadSeparator,
} from 'dashboard/store/modules/conversations/helpers';
import {
  buildCanonicalConversationRoute,
  getThreadAtConversationId,
  getThreadAtForCanonicalRedirect,
  shouldCanonicalizeThreadRoute,
  THREAD_AT_QUERY_KEY,
} from 'dashboard/helper/threadConversationRoute';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings, isOnExpandedLayout } =
      useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      isOnExpandedLayout,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      isResolvingConversation: false,
      lastResolvedKey: null,
      resolveGeneration: 0,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },
  watch: {
    '$route.fullPath'() {
      this.resolveAndActivateConversation();
    },
    inboxId: {
      immediate: true,
      handler(inboxId) {
        this.$store.dispatch('setActiveInbox', inboxId);
      },
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.resolveAndActivateConversation();
  },

  methods: {
    onConversationLoad() {
      this.resolveAndActivateConversation();
    },
    buildResolveKey() {
      const messageId = this.$route.query.messageId || '';
      const threadAt = this.$route.query.thread_at || '';
      return `${this.conversationId}|${messageId}|${threadAt}`;
    },
    isThreadAtReady(scrollToConversationId) {
      if (!scrollToConversationId) return true;

      const chat = this.currentChat;
      const activeId = chat.active_conversation_id || chat.id;
      if (scrollToConversationId === activeId) return true;

      return hasThreadSeparator(chat, scrollToConversationId);
    },
    isConversationReady(
      listRow,
      targetMessageId,
      scrollToConversationId = null
    ) {
      if (!listRow || listRow.id !== this.currentChat.id) return false;
      if (!this.currentChat.dataFetched || !this.currentChat.messages?.length) {
        return false;
      }
      if (targetMessageId) {
        return this.currentChat.messages.some(
          message => message.id === targetMessageId
        );
      }
      if (!scrollToConversationId && this.currentChat.hasNewerMessages) {
        return false;
      }
      return this.isThreadAtReady(scrollToConversationId);
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      return resolveThreadListRow(this.chatList, conversationId);
    },
    async resolveAndActivateConversation() {
      if (!this.conversationId) {
        this.$store.dispatch('clearSelectedState');
        this.lastResolvedKey = null;
        return;
      }

      const resolveKey = this.buildResolveKey();
      const targetMessageId = this.$route.query.messageId
        ? Number(this.$route.query.messageId)
        : null;
      const scrollToConversationId = getThreadAtConversationId(this.$route);
      const listRowPreview = this.findConversation();

      if (
        this.lastResolvedKey === resolveKey &&
        this.isConversationReady(
          listRowPreview,
          targetMessageId,
          scrollToConversationId
        )
      ) {
        return;
      }

      const resolveGeneration = this.resolveGeneration + 1;
      this.resolveGeneration = resolveGeneration;
      this.isResolvingConversation = true;

      try {
        const requestedId = parseInt(this.conversationId, 10);
        let listRow = listRowPreview;

        if (!listRow) {
          const fetched = await this.$store.dispatch(
            'getConversation',
            requestedId
          );
          if (resolveGeneration !== this.resolveGeneration) return;
          listRow = resolveThreadListRowFromFetched(
            this.chatList,
            requestedId,
            fetched
          );
          if (listRow && fetched) {
            listRow = {
              ...listRow,
              active_conversation_id:
                listRow.active_conversation_id ??
                fetched.active_conversation_id,
              contact_inbox_id:
                listRow.contact_inbox_id ?? fetched.contact_inbox_id,
            };
          }
        }

        if (!listRow) return;

        if (shouldCanonicalizeThreadRoute(requestedId, listRow)) {
          const canonicalRoute = buildCanonicalConversationRoute(
            this.$route,
            listRow
          );
          if (canonicalRoute) {
            const threadAt = getThreadAtForCanonicalRedirect(
              requestedId,
              listRow
            );
            const { [THREAD_AT_QUERY_KEY]: _threadAt, ...restQuery } =
              this.$route.query;
            await this.$router.replace({
              ...canonicalRoute,
              query: {
                ...restQuery,
                ...(threadAt
                  ? { [THREAD_AT_QUERY_KEY]: String(threadAt) }
                  : {}),
              },
            });
            return;
          }
        }

        if (
          this.isConversationReady(
            listRow,
            targetMessageId,
            scrollToConversationId
          )
        ) {
          if (listRow.contact_inbox_id) {
            await this.$store.dispatch(
              'syncActiveConversationDetails',
              listRow.id
            );
          }

          if (scrollToConversationId && !targetMessageId) {
            await this.$store.dispatch('ensureThreadConversationVisible', {
              listRowId: listRow.id,
              scrollToConversationId,
            });
          } else if (targetMessageId) {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
              messageId: targetMessageId,
            });
          } else {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
          }
          if (resolveGeneration === this.resolveGeneration) {
            this.lastResolvedKey = resolveKey;
          }
          return;
        }

        await this.$store.dispatch('setActiveChat', {
          data: listRow,
          after: targetMessageId,
          scrollToConversationId: targetMessageId
            ? null
            : scrollToConversationId,
        });
        if (resolveGeneration !== this.resolveGeneration) return;

        if (targetMessageId) {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
            messageId: targetMessageId,
          });
        }
        if (resolveGeneration === this.resolveGeneration) {
          this.lastResolvedKey = resolveKey;
        }
      } finally {
        this.isResolvingConversation = false;
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
